import React, {
  useState,
  useEffect,
  Fragment,
  createContext,
  useContext,
} from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  EllipsisVerticalIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";
import { useStore } from "../contexts/StoreContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchForms } from "../api/queries";
import { deleteForm } from "../api/mutations";
import { QRCodeSVG } from "qrcode.react";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { Menu, Transition } from "@headlessui/react";
import DeleteAlert from "../components/DeleteAlert";
import TrialEndedAlert from "../components/TrialEndedAlert";
import FormPreview from "./forms/components/FormPreview";
import EmptyState from "../components/EmptyState";
import Header from "../components/Header";
import Button from "../components/Button";
import { formatMessage } from "../utils/formatMessage";

// Create a localized language context for form previews
const LocalLanguageContext = createContext();

function LocalLanguageProvider({ children, targetLanguage }) {
  const [translations, setTranslations] = useState({});

  useEffect(() => {
    const loadTranslations = async (lang) => {
      try {
        const response = await import(`../locales/${lang}.json`);
        setTranslations(response.default);
      } catch (error) {
        console.error(`Failed to load translations for ${lang}:`, error);
        // Fallback to English if translation fails
        if (lang !== "en") {
          const enResponse = await import("../locales/en.json");
          setTranslations(enResponse.default);
        }
      }
    };

    if (targetLanguage) {
      loadTranslations(targetLanguage);
    }
  }, [targetLanguage]);

  const t = (key, variables) => {
    const keys = key.split(".");
    let value = translations;
    for (const k of keys) {
      value = value?.[k];
    }

    if (!value) return key;
    if (!variables) return value;

    return formatMessage(value, variables, targetLanguage);
  };

  return (
    <LocalLanguageContext.Provider value={{ language: targetLanguage, t }}>
      {children}
    </LocalLanguageContext.Provider>
  );
}

const useLocalLanguage = () => useContext(LocalLanguageContext);

// Component that renders a form with localized language
function FormCard({
  form,
  selectedStore,
  canManage,
  onEdit,
  onDelete,
  onCopyUrl,
  onDownloadQR,
}) {
  // Global UI language for the action menu chrome, so it follows the
  // language toggle. The form's own language is used for the preview only.
  const { t } = useLanguage();
  const { t: formT } = useLocalLanguage();

  return (
    <div className="rounded-lg border border-gray-200 bg-white pb-4 shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden">
      <div className="bg-gray-200 pl-4 p-2">
        <div className="flex justify-between items-center">
          <div className="flex space-x-3">
            {form.pass.name} - Form #{form.id}
          </div>
          <div className="flex space-x-3">
            <Menu as="div" className="relative inline-block text-left">
              <Menu.Button className="p-2 text-gray-500 hover:text-gray-700">
                <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-50 mt-2 p-1 whitespace-nowrap origin-top-right rounded-md bg-white shadow-lg ring-1 ring-gray-900/5 focus:outline-hidden">
                  {/* Edit/delete are admin-only on the API; copying the URL
                      and downloading the QR are for everyone. Not rendered at
                      all for a member — CSS-hidden Menu.Items stay reachable by
                      arrow keys, so Enter would still fire them. */}
                  {canManage && (
                    <div className="border-b border-gray-200">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => onEdit(form)}
                            className={`flex w-full items-center px-3 py-1 text-sm leading-6 text-indigo-600 ${active ? "bg-gray-50" : ""}`}
                          >
                            <PencilIcon className="h-5 w-5 mr-2" />
                            {t("forms.actions.edit")}
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => onDelete(form.id)}
                            className={`flex w-full items-center px-3 py-1 text-sm leading-6 text-red-600 ${active ? "bg-gray-50" : ""}`}
                          >
                            <TrashIcon className="h-5 w-5 mr-2" />
                            {t("forms.actions.delete")}
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  )}
                  <div className="px-1 py-1 ">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => onCopyUrl(form)}
                          className={`flex w-full items-center px-3 py-1 text-sm leading-6 text-gray-900 ${active ? "bg-gray-50" : ""}`}
                        >
                          <LinkIcon className="h-5 w-5 mr-2" />
                          {t("forms.list.copyURL")}
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => onDownloadQR(form, "svg")}
                          className={`flex w-full items-center px-3 py-1 text-sm leading-6 text-gray-900 ${active ? "bg-gray-50" : ""}`}
                        >
                          <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                          {t("forms.list.downloadQRCode")}
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => onDownloadQR(form, "png")}
                          className={`flex w-full items-center px-3 py-1 text-sm leading-6 text-gray-900 ${active ? "bg-gray-50" : ""}`}
                        >
                          <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                          {t("forms.list.downloadQRCodePNG")}
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>
      <div className="px-4">
        <div className="pt-4 max-w-md mx-auto">
          <FormPreview
            selectedStore={selectedStore}
            form={form}
            fields={form.content.fields}
            description={form.description}
            formData={{
              description: form.description,
              submitText: form.submit_text,
              backgroundColor: form.background_color,
              textColor: form.text_color,
            }}
            submitText={form.submit_text}
            formT={formT}
          />
        </div>
      </div>
      {/* Actions */}
      <div id={`qr-${form.id}`} className="hidden">
        <QRCodeSVG
          value={`${window.location.origin}/f/${form.uuid}`}
          size={64}
          level="L"
        />
      </div>
    </div>
  );
}

export default function Forms() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { selectedStore, canManageStore } = useStore();
  const queryClient = useQueryClient();
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [isTrialEndedAlertOpen, setIsTrialEndedAlertOpen] = useState(false);
  const [deleteFormId, setDeleteFormId] = useState(null);

  // Fetch forms
  const { data: forms, isLoading } = useQuery({
    queryKey: ["forms", selectedStore?.id],
    queryFn: () => fetchForms(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  const isInTrialPeriod = () => {
    if (!selectedStore?.trial_expires_at) return false;
    const trialEndDate = new Date(selectedStore.trial_expires_at);
    trialEndDate.setDate(trialEndDate.getDate());
    return new Date() < trialEndDate;
  };

  const handleOpenModal = (form) => {
    if (
      selectedStore?.subscription_status !== "active" &&
      selectedStore?.subscription_status !== "trialing" &&
      selectedStore?.subscription_status !== "past_due" &&
      !isInTrialPeriod()
    ) {
      setIsTrialEndedAlertOpen(true);
      return;
    } else {
      if (form) {
        navigate(`/forms/${form.id}/edit`);
      } else {
        navigate(`/forms/new`);
      }
    }
  };

  const downloadQRCode = async (form, type) => {
    const qrValue = `${window.location.origin}/f/${form?.uuid}`;

    if (type === "svg") {
      const svgString = document.getElementById(`qr-${form.id}`).innerHTML;
      const blob = new Blob([svgString], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `qr-code-${form.id}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else if (type === "png") {
      try {
        const svgElement = document
          .getElementById(`qr-${form.id}`)
          .querySelector("svg");
        const svgData = new XMLSerializer().serializeToString(svgElement);

        const svgString = `
                    <svg width="1024" height="1024" xmlns="http://www.w3.org/2000/svg">
                        <rect width="1024" height="1024" fill="white"/>
                        <g transform="scale(${1024 / svgElement.viewBox.baseVal.width})">
                            ${svgData.replace(/<svg[^>]*>|<\/svg>/g, "")}
                        </g>
                    </svg>
                `;

        const canvas = document.createElement("canvas");
        canvas.width = 1024;
        canvas.height = 1024;
        const ctx = canvas.getContext("2d");

        const img = new Image();
        const blob = new Blob([svgString], {
          type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);

        await new Promise((resolve, reject) => {
          img.onload = () => {
            ctx.fillStyle = "white";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            resolve();
          };
          img.onerror = reject;
          img.src = url;
        });

        URL.revokeObjectURL(url);

        const pngUrl = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = pngUrl;
        link.download = `qr-code-${form.id}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error generating PNG:", error);
        alert("Failed to generate PNG. Please try again.");
      }
    }
  };

  const deleteFormMutation = useMutation({
    mutationFn: deleteForm,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forms", selectedStore?.id] });
    },
  });

  const handleDeleteForm = async (formId) => {
    if (window.confirm(t("forms.list.deleteConfirm"))) {
      try {
        await deleteFormMutation.mutateAsync({
          storeId: selectedStore.id,
          formId,
        });
      } catch (error) {
        console.error("Error deleting form:", error);
      }
    }
  };

  const copyFormUrl = (form) => {
    const url = `${window.location.origin}/f/${form.uuid}`;
    navigator.clipboard.writeText(url);
  };

  return (
    <>
      <div className="max-w-8xl mx-auto space-y-6 lg:space-y-8">
        <Header
          icon={DocumentTextIcon}
          title={t("forms.title")}
          description={t("forms.description")}
          rightSection={
            canManageStore && (
              <Button onClick={() => handleOpenModal()} icon={PlusIcon}>
                {t("forms.list.newButton")}
              </Button>
            )
          }
        />

        {forms?.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:gap-8 md:grid-cols-2 xl:grid-cols-3">
            {forms?.map((form) => (
              <LocalLanguageProvider
                key={form.id}
                targetLanguage={form.lang || "en"}
              >
                <FormCard
                  form={form}
                  selectedStore={selectedStore}
                  canManage={canManageStore}
                  onEdit={handleOpenModal}
                  onDelete={(formId) => {
                    setIsDeleteAlertOpen(true);
                    setDeleteFormId(formId);
                  }}
                  onCopyUrl={copyFormUrl}
                  onDownloadQR={downloadQRCode}
                />
              </LocalLanguageProvider>
            ))}
          </div>
        )}
      </div>

      <DeleteAlert
        isOpen={isDeleteAlertOpen}
        onClose={() => setIsDeleteAlertOpen(false)}
        onConfirm={() => {
          deleteFormMutation.mutate({
            storeId: selectedStore.id,
            formId: deleteFormId,
          });
          setIsDeleteAlertOpen(false);
        }}
      />
      <TrialEndedAlert
        isOpen={isTrialEndedAlertOpen}
        onClose={() => setIsTrialEndedAlertOpen(false)}
        subscriptionStatus={selectedStore?.subscription_status}
      />
    </>
  );
}
